import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './ReturnHandler.css';

/**
 * ReturnHandler Component
 * 
 * Handles the return flow after PayPal payment completion.
 * PayPal redirects to: https://dn9souybgk5tq.cloudfront.net/subscription-return?subscription_id={id}
 * 
 * This component:
 * - Extracts subscription_id from URL query parameters
 * - Polls GET /subscriptions endpoint every 2 seconds
 * - Shows success message when status is "active"
 * - Shows pending message if not active after 30 seconds
 * - Redirects to subscription page after 5 seconds
 */
const ReturnHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState<'checking' | 'success' | 'pending' | 'error'>('checking');
  const [message, setMessage] = useState<string>('Verifying your subscription...');
  const [countdown, setCountdown] = useState<number>(5);
  
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingStartTimeRef = useRef<number>(Date.now());
  const hasRedirectedRef = useRef<boolean>(false);
  const subscriptionIdRef = useRef<string | null>(null);
  const initCalledRef = useRef<boolean>(false);

  useEffect(() => {
    const subscriptionId = searchParams.get('subscription_id');

    if (!subscriptionId) {
      setStatus('error');
      setMessage('No subscription ID provided. Redirecting to subscription page...');
      scheduleRedirect();
      return;
    }

    subscriptionIdRef.current = subscriptionId;

    // For PayPal account payments, the page is fully redirected and onApprove never fires.
    // Call initializeSubscription here using the planId saved before the redirect.
    const savedPlanId = sessionStorage.getItem('pendingPlanId');
    const savedSubId = sessionStorage.getItem('pendingSubscriptionId');
    if (savedPlanId && savedSubId === subscriptionId && !initCalledRef.current) {
      initCalledRef.current = true;
      sessionStorage.removeItem('pendingPlanId');
      sessionStorage.removeItem('pendingSubscriptionId');
      import('../../services/api').then(({ apiService }) => {
        apiService.initializeSubscription(subscriptionId, savedPlanId).catch((err) => {
          console.warn('initializeSubscription failed (non-critical, webhook will recover):', err);
        });
      });
    }

    // Start polling for subscription status
    startPolling();

    // Cleanup on unmount
    return () => {
      clearPolling();
      clearRedirect();
      clearCountdown();
    };
  }, [searchParams]);

  const startPolling = () => {
    pollingStartTimeRef.current = Date.now();
    
    // Poll immediately
    checkSubscriptionStatus();
    
    // Then poll every 2 seconds
    pollingIntervalRef.current = setInterval(() => {
      checkSubscriptionStatus();
    }, 2000);
  };

  const checkSubscriptionStatus = async () => {
    try {
      const { apiService } = await import('../../services/api');
      const data = await apiService.getSubscription();
      
      const elapsedTime = Date.now() - pollingStartTimeRef.current;
      const thirtySeconds = 30 * 1000;
      
      const isCorrectSubscription = data.subscription.paypalSubscriptionId === subscriptionIdRef.current;

      if (data.subscription.status === 'active' && isCorrectSubscription) {
        // Success! The correct subscription is now active
        setStatus('success');
        setMessage('Success! Your subscription is now active.');
        clearPolling();
        scheduleRedirect();
      } else if (data.subscription.status === 'pending' || (data.subscription.status === 'active' && !isCorrectSubscription)) {
        // Subscription initialized but not yet activated by webhook
        if (elapsedTime >= thirtySeconds) {
          setStatus('pending');
          setMessage(
            'Your subscription is being activated. This may take a few minutes. ' +
            'You can check your subscription status on the subscription page.'
          );
          clearPolling();
          scheduleRedirect();
        }
        // Otherwise, keep polling - webhook should arrive soon
      } else if (elapsedTime >= thirtySeconds) {
        // 30 seconds elapsed and still not active
        setStatus('pending');
        setMessage(
          'Your payment is being processed. This may take a few minutes. ' +
          'You can check your subscription status on the subscription page.'
        );
        clearPolling();
        scheduleRedirect();
      }
      // Otherwise, keep polling
    } catch (error) {
      console.error('Failed to check subscription status:', error);
      
      // Only show error if we've been polling for a while
      const elapsedTime = Date.now() - pollingStartTimeRef.current;
      if (elapsedTime >= 10000) { // 10 seconds
        setStatus('error');
        setMessage(
          'Unable to verify subscription status. Please check your subscription page.'
        );
        clearPolling();
        scheduleRedirect();
      }
    }
  };

  const scheduleRedirect = () => {
    if (hasRedirectedRef.current) return;
    
    // Start countdown
    setCountdown(5);
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    // Schedule redirect after 5 seconds
    redirectTimeoutRef.current = setTimeout(() => {
      if (!hasRedirectedRef.current) {
        hasRedirectedRef.current = true;
        navigate('/dashboard?view=subscription');
      }
    }, 5000);
  };

  const clearPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const clearRedirect = () => {
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }
  };

  const clearCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const handleManualRedirect = () => {
    if (!hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      clearPolling();
      clearRedirect();
      clearCountdown();
      navigate('/dashboard?view=subscription');
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'checking':
        return '⏳';
      case 'success':
        return '✓';
      case 'pending':
        return '⏱️';
      case 'error':
        return '⚠️';
      default:
        return '';
    }
  };

  const getStatusClass = () => {
    return `return-handler-${status}`;
  };

  return (
    <div className="return-handler-container">
      <div className={`return-handler-card ${getStatusClass()}`}>
        <div className="status-icon">{getStatusIcon()}</div>
        <h1 className="status-title">
          {status === 'checking' && 'Verifying Subscription'}
          {status === 'success' && 'Subscription Activated!'}
          {status === 'pending' && 'Payment Processing'}
          {status === 'error' && 'Verification Issue'}
        </h1>
        <p className="status-message">{message}</p>
        
        {status !== 'checking' && (
          <div className="redirect-info">
            <p className="redirect-message">
              Redirecting to subscription page in {countdown} second{countdown !== 1 ? 's' : ''}...
            </p>
            <button 
              onClick={handleManualRedirect}
              className="manual-redirect-button"
            >
              Go to Subscription Page Now
            </button>
          </div>
        )}
        
        {status === 'checking' && (
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReturnHandler;
